import api from './api';

const chatService = {
  /**
   * Send a message to the AI assistant
   * @param {string} message - User's message
   * @param {string} sessionId - Chat session identifier
   */
  async sendMessage(message, sessionId, screen = null) {
    const response = await api.post('/chat/message', { message, sessionId, screen });
    return response.data;
  },

  /**
   * Stream a message response via Server-Sent Events.
   * @param {string} message - User's message
   * @param {string} sessionId - Chat session identifier
   * @param {object|null} screen - Where the user is: { path, search } (KC-B)
   * @param {object} handlers - { onDelta, onToolUse, onAction, onUsage, onDone, onError }
   */
  async streamMessage(message, sessionId, screen, { onDelta, onToolUse, onAction, onUsage, onDone, onError }) {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

    try {
      const response = await fetch(`${API_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ message, sessionId, screen })
      });

      if (!response.ok) {
        // Rate limit or other error — try to parse JSON body
        let errMsg = 'Stream request failed';
        let errBody = null;
        try {
          errBody = await response.json();
          errMsg = errBody.message || errMsg;
        } catch (e) { /* not JSON */ }
        // Forward the full body so callers can distinguish monthly vs minute limits
        onError?.(errMsg, errBody);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events (separated by \n\n)
        const events = buffer.split('\n\n');
        buffer = events.pop(); // keep last incomplete chunk

        for (const rawEvent of events) {
          const line = rawEvent.trim();
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'delta') onDelta?.(data.text);
            else if (data.type === 'tool_use') onToolUse?.(data);
            else if (data.type === 'action') onAction?.(data);
            else if (data.type === 'usage') onUsage?.(data);
            else if (data.type === 'done') onDone?.(data);
            else if (data.type === 'error') onError?.(data.message, data);
          } catch (e) {
            // ignore malformed events
          }
        }
      }

      // Flush any remaining buffer
      if (buffer.trim().startsWith('data: ')) {
        try {
          const data = JSON.parse(buffer.trim().slice(6));
          if (data.type === 'delta') onDelta?.(data.text);
          else if (data.type === 'usage') onUsage?.(data);
          else if (data.type === 'done') onDone?.(data);
        } catch (e) {}
      }
    } catch (err) {
      onError?.(err.message || 'Connection lost');
    }
  },

  /**
   * Get chat history for a specific session
   * @param {string} [sessionId] - Session ID to load (null returns empty)
   */
  async getHistory(sessionId = null) {
    const params = sessionId ? { sessionId } : {};
    const response = await api.get('/chat/history', { params });
    return response.data;
  },

  /**
   * Start a new chat session (messages preserved in DB)
   * @returns {Promise<{ success: boolean, data: { sessionId: string } }>}
   */
  async resetSession() {
    const response = await api.post('/chat/reset');
    return response.data;
  }
};

export default chatService;
